import { a4 as createStoreContext, a5 as HovercardScopedContextProvider, a6 as HovercardContextProvider, a7 as createDialogComponent, J as forwardRef, K as createElement, L as createHook, O as invariant, a8 as useWrapElement, k as jsxRuntimeExports, P as useStoreState, a9 as useHovercard, aa as isFalsyBooleanCallback, ab as contains, ac as sync, ad as chain, ae as useEvent, af as useHovercardAnchor, ag as createStore, ah as createHovercardStore, ai as defaultValue, aj as useStore, ak as useStoreProps, al as useHovercardStoreProps } from "./doenetml-drNYjJiF.js";
import { useRef, useEffect } from "react";
const ctx = createStoreContext([HovercardContextProvider], [HovercardScopedContextProvider]);
const useTooltipProviderContext = ctx.useProviderContext;
const TooltipContextProvider = ctx.ContextProvider;
const TooltipScopedContextProvider = ctx.ScopedContextProvider;
const TagName$1 = "div";
const useTooltip = createHook(function useTooltip2({ store, portal = true, gutter = 8, preserveTabOrder = false, hideOnHoverOutside = true, hideOnInteractOutside = true, ...props }) {
  const context = useTooltipProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Tooltip must receive a `store` prop or be wrapped in a TooltipProvider component.");
  props = useWrapElement(props, (element) => /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipScopedContextProvider, {
    value: store,
    children: element
  }), [store]);
  props = {
    role: useStoreState(store, (state) => state.type === "description" ? "tooltip" : "none"),
    ...props
  };
  props = useHovercard({
    ...props,
    store,
    portal,
    gutter,
    preserveTabOrder,
    hideOnHoverOutside(event) {
      if (isFalsyBooleanCallback(hideOnHoverOutside, event)) return false;
      const anchorElement = store?.getState().anchorElement;
      if (!anchorElement) return true;
      if ("focusVisible" in anchorElement.dataset) return false;
      return true;
    },
    hideOnInteractOutside: (event) => {
      if (isFalsyBooleanCallback(hideOnInteractOutside, event)) return false;
      const anchorElement = store?.getState().anchorElement;
      if (!anchorElement) return true;
      if (contains(anchorElement, event.target)) return false;
      return true;
    }
  });
  return props;
});
const Tooltip = createDialogComponent(forwardRef(function Tooltip2(props) {
  return createElement(TagName$1, useTooltip(props));
}), useTooltipProviderContext);
const TagName = "div";
const globalStore = createStore({ activeStore: null });
const hidingStores = /* @__PURE__ */ new WeakSet();
function createRemoveStoreCallback(store) {
  return () => {
    const { activeStore } = globalStore.getState();
    if (activeStore !== store) return;
    globalStore.setState("activeStore", null);
  };
}
function hideStore(store) {
  if (!store) return;
  hidingStores.add(store);
  store.hide();
  queueMicrotask(() => hidingStores.delete(store));
}
const useTooltipAnchor = createHook(function useTooltipAnchor2({ store, showOnHover = true, ...props }) {
  const context = useTooltipProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "TooltipAnchor must receive a `store` prop or be wrapped in a TooltipProvider component.");
  const canShowOnHoverRef = useRef(false);
  useEffect(() => {
    return sync(store, ["mounted"], (state) => {
      if (state.mounted) return;
      canShowOnHoverRef.current = false;
    });
  }, [store]);
  useEffect(() => {
    if (!store) return;
    const removeStore = createRemoveStoreCallback(store);
    return chain(removeStore, sync(store, ["mounted"], (state) => {
      if (!store) return;
      if (state.mounted) {
        const { activeStore } = globalStore.getState();
        if (activeStore !== store) {
          if (hidingStores.has(store)) return;
          hideStore(activeStore);
        }
        return globalStore.setState("activeStore", store);
      }
      const id = setTimeout(removeStore, store.getState().skipTimeout);
      return () => clearTimeout(id);
    }));
  }, [store]);
  const onMouseEnterProp = props.onMouseEnter;
  const onMouseEnter = useEvent((event) => {
    onMouseEnterProp?.(event);
    canShowOnHoverRef.current = true;
  });
  const onFocusVisibleProp = props.onFocusVisible;
  const onFocusVisible = useEvent((event) => {
    onFocusVisibleProp?.(event);
    if (event.defaultPrevented) return;
    store?.setAnchorElement(event.currentTarget);
    store?.show();
  });
  const onBlurProp = props.onBlur;
  const onBlur = useEvent((event) => {
    onBlurProp?.(event);
    if (event.defaultPrevented) return;
    const { activeStore } = globalStore.getState();
    canShowOnHoverRef.current = false;
    if (activeStore === store) globalStore.setState("activeStore", null);
  });
  const labelledBy = useStoreState(store, (state) => state.type === "label" ? state.contentElement?.id : void 0);
  props = {
    "aria-labelledby": props["aria-label"] == null ? labelledBy : void 0,
    ...props,
    onMouseEnter,
    onFocusVisible,
    onBlur
  };
  props = useHovercardAnchor({
    store,
    showOnHover(event) {
      if (!canShowOnHoverRef.current) return false;
      if (isFalsyBooleanCallback(showOnHover, event)) return false;
      const { activeStore } = globalStore.getState();
      if (!activeStore) return true;
      store?.show();
      return false;
    },
    ...props
  });
  return props;
});
const TooltipAnchor = forwardRef(function TooltipAnchor2(props) {
  return createElement(TagName, useTooltipAnchor(props));
});
function createTooltipStore(props = {}) {
  if (process.env.NODE_ENV !== "production") {
    if (props.type === "label") console.warn("The `type` option on the tooltip store is deprecated.", "Render a visually hidden label or use the `aria-label` or `aria-labelledby` attributes on the anchor element instead.", "See https://ariakit.com/components/tooltip#tooltip-anchors-must-have-accessible-names");
  }
  const syncState = props.store?.getState();
  const hovercard = createHovercardStore({
    ...props,
    placement: defaultValue(props.placement, syncState?.placement, "top"),
    hideTimeout: defaultValue(props.hideTimeout, syncState?.hideTimeout, 0)
  });
  const tooltip = createStore({
    ...hovercard.getState(),
    type: defaultValue(props.type, syncState?.type, "description"),
    skipTimeout: defaultValue(props.skipTimeout, syncState?.skipTimeout, 300)
  }, hovercard, props.store);
  return {
    ...hovercard,
    ...tooltip
  };
}
function useTooltipStoreProps(store, update, props) {
  useStoreProps(store, props, "type");
  useStoreProps(store, props, "skipTimeout");
  return useHovercardStoreProps(store, update, props);
}
function useTooltipStore(props = {}) {
  const [store, update] = useStore(createTooltipStore, props);
  return useTooltipStoreProps(store, update, props);
}
function TooltipProvider(props = {}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContextProvider, {
    value: useTooltipStore(props),
    children: props.children
  });
}
export {
  TooltipProvider as T,
  TooltipAnchor as a,
  Tooltip as b
};
